import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '@shared/state/AuthContext'
import { addExternalToLibrary, sendToMiroQueue, type ImageRef } from '@shared/services'
import {
  MARKETS,
  fullSize,
  marketOf,
  keepPictures,
  openProduct,
  pictureKey,
  searchAmazon,
  AmazonError,
  type FoundPage,
  type FoundProduct,
  type Market,
  type OpenedProduct,
  type Picked,
} from '@shared/services/amazonService'
import { WwLoaderFill } from '@shared/components/WwLoader'
import type { MiroSent } from '../hooks/useMiroSendProgress'
import { useMiroSendProgress } from '../hooks/useMiroSendProgress'
import { useClipSettled } from '../hooks/useClipSettled'
import { putClip } from '@shared/services/projectClip'
import { usePaintSelection } from '@shared/hooks/usePaintSelection'
import { GooeyBackground } from '../components/GooeyBackground'
import { MarketPicker } from '../components/MarketPicker'
import { PictureViewer } from '../components/PictureViewer'
import './amazon.css'

/**
 * Amazon Carousel
 * -------------------------------------------------------------
 * Search Amazon, see every result's main picture, open a product to see all
 * of its pictures, tick the ones wanted, send them to Miro.
 *
 * WHAT COSTS MONEY, AND WHAT DOES NOT
 *
 * A search is one request of the Rainforest plan, and so is each further page
 * and each product opened. Nothing else is: the pictures load straight from
 * Amazon's image host, and looking through a product that is already open,
 * ticking, un-ticking and sending cost nothing. The same search or product
 * again within a week is read from the server's cache and is free too.
 *
 * Which is why more results come from a button and not from scrolling to the
 * bottom: a page that quietly spends as it is scrolled is a page whose bill
 * nobody can predict.
 *
 * PICKING, AND OPENING
 *
 * A click on a card picks it; pressing and sweeping across cards picks every
 * card swept over, the same gesture as the library. Opening a product is the
 * "View details" button that appears over a card under the pointer -- a
 * separate, deliberate press, because opening one costs a request and a
 * click meant as a pick should never spend one (Huanrui, 2026-09-29).
 *
 * WHAT A TICK MEANS
 *
 * On a card, the product's main picture. Inside a product, whichever picture
 * it is on. A main picture ticked in either place is ticked in both, because
 * a tick is keyed by the picture's own name rather than by the address it
 * happened to be shown at (a 320 px thumbnail on the card, the full picture
 * inside).
 */

/** Pages fetched by a new search, and by each Load more. */
const FIRST_PAGES = 3
const MORE_PAGES = 2

/** Exported for the full-screen library's product view (LibraryBrowser.tsx, 2026-10-21). */
export interface Held extends Picked {
  market: Market
  /** Something small to show in the tray. */
  thumb: string
}

const BAR_WORDS = {
  amazon: 'Search Amazon for a product',
  google: 'Search Google Images',
  unsplash: 'Search Unsplash',
} as const

export function AmazonPage({
  lead,
  initialTerm = '',
  onTyped,
  autoSearch = false,
  onSearched,
  onSubmitInstead,
  barSource = 'amazon',
  market: sharedMarket,
  onMarket,
}: {
  /** In front of the search box: the External Library's source menu (2026-10-14). */
  lead?: ReactNode
  /** In the box when the page opens -- the words carried over from another source. Not searched until Search is pressed. */
  initialTerm?: string
  /** Told the words in the box as they are typed, so a switch of source can carry them over. */
  onTyped?: (term: string) => void
  /** Search `initialTerm` as soon as the page opens, already docked (a source switched, then Search). */
  autoSearch?: boolean
  /** Told when this page has searched, so the page around it knows its results are worth keeping. */
  onSearched?: () => void
  /**
   * Search is pressed while another source is chosen in the menu: the words go
   * there instead, and this page -- its results still on screen until then --
   * makes way (External Library, 2026-10-14).
   */
  onSubmitInstead?: (term: string) => void
  /**
   * The source the bar is drawn for: the one chosen in the menu, which may
   * not be this page's until Search is pressed (2026-10-14). Its words and
   * whether it has a country follow the choice at once.
   */
  barSource?: 'amazon' | 'google' | 'unsplash'
  /** The country, kept by the page around this one so a switch carries it. */
  market?: Market
  onMarket?: (next: Market) => void
} = {}) {
  const { user } = useAuth()
  // The United States every time the page opens (Huanrui, 2026-09-29), and
  // only a search changes what is on screen: picking another Amazon waits for
  // Search to be pressed.
  const [ownMarket, setOwnMarket] = useState<Market>('us')
  const market = sharedMarket ?? ownMarket
  const setMarket = onMarket ?? setOwnMarket
  const [draft, setDraft] = useState(initialTerm)

  /** What the results on screen are the answer to. */
  const [asked, setAsked] = useState<{ market: Market; term: string } | null>(null)
  const [results, setResults] = useState<FoundProduct[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')

  /** Brands learnt by opening products. A search carries none. */
  const [brands, setBrands] = useState<Map<string, string>>(new Map())

  const [open, setOpen] = useState<FoundProduct | null>(null)
  /** A product's picture opened larger from its card (2026-11-02), without opening the product. */
  const [viewing, setViewing] = useState<string | null>(null)
  const [picked, setPicked] = useState<Map<string, Held>>(new Map())

  /* ---------- searching ---------- */

  /**
   * Whether pages can be asked for several at once. Believed until the plan
   * says otherwise; then one page per press, as it was before, for the rest
   * of the visit.
   */
  const together = useRef(true)

  /**
   * Fetch pages `from` onwards, `count` of them, at the same time.
   *
   * Three pages to start with and two more each time (Huanrui, 2026-09-29):
   * a page is about 70 products and each is one request, so the first
   * search costs three -- and since they are asked for together, it takes
   * about as long as one did. Only the pages that came back in order are
   * shown; a later one that arrived without the page before it waits for the
   * next press, and is free then, because the server kept it.
   */
  const run = useCallback(
    async (wanted: { market: Market; term: string }, from: number, count: number) => {
      setSearching(true)
      setError('')
      // A new search clears the old answer at once, so the wait is shown
      // rather than the last results sitting there looking current.
      if (from === 1) {
        setResults([])
        setPage(0)
      }
      const pages = Array.from({ length: together.current ? count : 1 }, (_, i) => from + i)
      const settled = await Promise.allSettled(
        pages.map((one) => searchAmazon(wanted.market, wanted.term, one)),
      )
      const arrived: FoundPage[] = []
      for (const outcome of settled) {
        if (outcome.status !== 'fulfilled') break
        arrived.push(outcome.value)
      }
      const refused = settled.find((one) => one.status === 'rejected') as PromiseRejectedResult | undefined
      if (refused && (arrived.length > 0 || (refused.reason as AmazonError)?.status === 429)) {
        // Some came back and some did not, or the plan said "too many at
        // once": from now on, one page at a time.
        together.current = false
      }
      if (arrived.length === 0) {
        const reason = refused?.reason
        setError(
          (reason as AmazonError)?.status === 429
            ? 'Amazon is busy with other searches on this plan. Press Search again in a moment.'
            : reason instanceof Error
              ? reason.message
              : 'The search did not work',
        )
        setSearching(false)
        return
      }
      const last = arrived[arrived.length - 1]
      setAsked(wanted)
      setPage(last.page)
      setTotalPages(Math.max(...arrived.map((one) => one.totalPages)))
      setResults((was) => {
        // A page can repeat a product from the one before.
        const seen = new Set(from === 1 ? [] : was.map((one) => one.asin))
        const next = from === 1 ? [] : [...was]
        for (const one of arrived.flatMap((found) => found.results)) {
          if (seen.has(one.asin)) continue
          seen.add(one.asin)
          next.push(one)
        }
        return next
      })
      setSearching(false)
    },
    [],
  )

  /**
   * The bar starts in the middle of the empty page and, at the first search,
   * glides up to its place above the results (Huanrui, 2026-10-02) -- and
   * stays there for the rest of the visit. Where it was is taken just before
   * it moves, so the move can be played from there (useLayoutEffect below).
   */
  const opensSearching = autoSearch && Boolean(initialTerm.trim())
  const [docked, setDocked] = useState(opensSearching)
  const bar = useRef<HTMLFormElement | null>(null)
  const barWas = useRef<DOMRect | null>(null)
  // The login page's light behind the empty page: it fades as the bar leaves
  // the middle, then goes, so it costs nothing while results are on screen.
  const [glow, setGlow] = useState(!opensSearching)
  // The empty page is one screen and holds still; once there are results it
  // scrolls as any page does.
  useEffect(() => {
    if (docked) return
    const page = document.documentElement
    const was = page.style.overflow
    page.style.overflow = 'hidden'
    return () => {
      page.style.overflow = was
    }
  }, [docked])
  useEffect(() => {
    if (!docked) return
    const timer = window.setTimeout(() => setGlow(false), 800)
    return () => window.clearTimeout(timer)
  }, [docked])

  const submit = () => {
    const term = draft.trim()
    if (!term || searching) return
    if (onSubmitInstead) {
      onSubmitInstead(term)
      return
    }
    onSearched?.()
    if (!docked) {
      barWas.current = bar.current?.getBoundingClientRect() ?? null
      setDocked(true)
    }
    void run({ market, term }, 1, FIRST_PAGES)
  }

  // Opened by Search on another source's page: searched at once.
  useEffect(() => {
    const term = initialTerm.trim()
    if (!opensSearching || !term) return
    onSearched?.()
    void run({ market, term }, 1, FIRST_PAGES)
    // Once, on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // Play the move: from where it was to where it is. Only its place is
  // animated here; its size and colour turn by CSS at the same time, which is
  // why this is an animation of its own rather than a style that would
  // override those transitions.
  useLayoutEffect(() => {
    const el = bar.current
    const was = barWas.current
    barWas.current = null
    if (!docked || !el || !was) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const now = el.getBoundingClientRect()
    const dx = was.left - now.left
    const dy = was.top - now.top
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return
    el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
      duration: 620,
      easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    })
  }, [docked])

  // Waits for Search: a different Amazon is a different search, and a search
  // costs requests, so it happens when it is asked for.
  const switchMarket = (next: Market) => {
    if (next === market) return
    setMarket(next)
  }

  /* ---------- ticking ---------- */

  const toggle = useCallback((key: string, held: Held) => {
    setPicked((was) => {
      const next = new Map(was)
      if (next.has(key)) next.delete(key)
      else next.set(key, held)
      return next
    })
  }, [])

  /** Pick or unpick several at once: the select bar and a sweep. */
  const setSome = useCallback((entries: [string, Held][], on: boolean) => {
    setPicked((was) => {
      const next = new Map(was)
      for (const [key, held] of entries) {
        if (on) next.set(key, held)
        else next.delete(key)
      }
      return next
    })
  }, [])

  const heldForCard = (product: FoundProduct): Held => ({
    url: fullSize(product.image),
    asin: product.asin,
    title: product.title,
    market: asked?.market ?? market,
    thumb: product.image,
  })

  const onPaint = useCallback(
    (product: FoundProduct, on: boolean) =>
      setSome([[pictureKey(product.image), heldForCard(product)]], on),
    // heldForCard reads only `asked` and `market`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSome, asked, market],
  )
  const paint = usePaintSelection<FoundProduct>(onPaint)

  /**
   * One card per product line, unless switched off (Huanrui, 2026-09-29):
   * flavours, sizes and multipacks of one product are one line; a different
   * kind of product from the same brand is another. The lines come from the
   * server, which has a model read the titles (routes/amazon.ts). On by
   * default and remembered.
   *
   * The first of each line stands for it, which is also the one Amazon ranked
   * highest. How many it stands for is shown on the card.
   */
  const [dedupe, setDedupe] = useState(true)
  const { shown, hiddenBehind } = useMemo(() => {
    if (!dedupe) return { shown: results, hiddenBehind: new Map<string, number>() }
    const first = new Map<string, string>()
    const hidden = new Map<string, number>()
    const out: FoundProduct[] = []
    for (const one of results) {
      const line = one.family ?? ''
      const leader = line ? first.get(line) : undefined
      if (leader) {
        hidden.set(leader, (hidden.get(leader) ?? 0) + 1)
        continue
      }
      if (line) first.set(line, one.asin)
      out.push(one)
    }
    return { shown: out, hiddenBehind: hidden }
  }, [results, dedupe])

  /** The first N on the page, as the library's select bar does it. */
  const pickFirst = (count: number) =>
    setSome(
      shown.slice(0, count).map((one) => [pictureKey(one.image), heldForCard(one)] as [string, Held]),
      true,
    )
  const selectSteps = [20, 50].filter((n) => n < shown.length)

  /* ---------- the tray ---------- */

  const [sending, setSending] = useState(false)
  /**
   * Also into the Internal Library (Audit), as they are (Huanrui, 2026-11-02).
   * Off every time: unticked again after each Send or Copy.
   */
  const [toLibrary, setToLibrary] = useState(false)
  const fileIfTicked = (pictures: { url: string; title: string }[]) => {
    if (!toLibrary) return
    addExternalToLibrary('amazon', asked?.term ?? '', pictures)
    setToLibrary(false)
  }
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  /** What was last sent to Miro, followed until it is on the board. */
  const [sent, setSent] = useState<MiroSent | null>(null)
  useMiroSendProgress(sent, setStatus, () => setSent(null))
  /** The copy this page made, until a project takes it; then its message goes. */
  const [copied, setCopied] = useState<{ token: string; text: string } | null>(null)
  useClipSettled(copied?.token ?? '', () => {
    setStatus((now) => (now && now.text === copied?.text ? null : now))
    setCopied(null)
  })
  const dismiss = () => {
    setStatus(null)
    setSent(null)
    setCopied(null)
  }

  const send = async () => {
    if (sending || picked.size === 0) return
    setSending(true)
    setStatus({ kind: 'ok', text: `Copying ${picked.size} from Amazon…` })
    try {
      const all = [...picked.values()]
      fileIfTicked(all.map((held) => ({ url: held.url, title: held.title })))
      const refs: ImageRef[] = []
      let failed = 0
      // Kept per Amazon, so each picture remembers which one it came from.
      for (const one of MARKETS) {
        const mine = all.filter((held) => held.market === one.value)
        if (mine.length === 0) continue
        const kept = await keepPictures(one.value, mine)
        refs.push(...kept.refs)
        failed += kept.failed
      }
      if (refs.length === 0) throw new Error('None of them could be copied from Amazon')
      const queue = await sendToMiroQueue('web', refs)
      // Sent means handed over: the picks go at once, as on the library pages,
      // rather than when the panel reports them placed -- which cleared
      // whatever was picked by then, possibly a different set.
      setPicked(new Map())
      setSent({ at: queue.createdAt, items: queue.items })
      const queued = queue.items.length
      setStatus({
        kind: failed > 0 ? 'err' : 'ok',
        text:
          (failed > 0 ? `${failed} could not be copied from Amazon. ` : '') +
          `${queued} image${queued === 1 ? '' : 's'} ready. Open the WonderWorks app on a Miro board to place them.`,
      })
    } catch (caught) {
      setStatus({ kind: 'err', text: caught instanceof Error ? caught.message : 'Could not send to Miro' })
    } finally {
      setSending(false)
    }
  }

  /**
   * Copy to project: the picks are written down and every project page offers
   * to bring them in. Nothing is fetched from Amazon until one says yes, and
   * then straight into that project (src/shared/services/projectClip.ts).
   */
  const copyToProject = () => {
    if (picked.size === 0) return
    try {
      fileIfTicked([...picked.values()].map((held) => ({ url: held.url, title: held.title })))
      const clip = putClip(
        'Amazon',
        [...picked.values()].map((held) => ({
          kind: 'amazon' as const,
          market: held.market,
          url: held.url,
          asin: held.asin,
          title: held.title,
          thumb: held.thumb,
        })),
      )
      const text = `${picked.size} copied. Open any project and they will be offered to its canvas.`
      setStatus({ kind: 'ok', text })
      setCopied({ token: clip.token, text })
      // Copied means written down: the picks go, as after sending to Miro.
      setPicked(new Map())
    } catch (error) {
      console.error('could not write the copy down', error)
      setStatus({ kind: 'err', text: 'This browser would not keep the copy' })
    }
  }

  // The way back up, once there is a way up to go back to: the same button,
  // in the same place, as the Audit library.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 700)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /* ---------- drawing ---------- */

  const shownTray = useMemo(() => [...picked.entries()].slice(0, 48), [picked])

  return (
    <div className="amz">
      {glow ? (
        <div className={`amz__glow${docked ? ' amz__glow--off' : ''}`} aria-hidden="true">
          <GooeyBackground />
        </div>
      ) : null}
      <div className={`amz__stage${docked ? '' : ' amz__stage--idle'}`}>
      <form
        className={`amz__bar${docked ? ' amz__bar--docked' : ''}`}
        ref={bar}
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        {lead}
        <svg className="amz__glass-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M15.5 15.5 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          className="amz__search"
          type="search"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value)
            onTyped?.(event.target.value)
          }}
          placeholder={BAR_WORDS[barSource]}
          aria-label={BAR_WORDS[barSource]}
          autoFocus
        />
        {barSource === 'unsplash' ? null : <MarketPicker value={market} onChange={switchMarket} />}
        <button type="submit" className="amz__go" disabled={searching || !draft.trim()}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>
      </div>

      {error ? <p className="amz__error">{error}</p> : null}

      {asked ? (
        <div className="amz__metarow">
          {/* The switch and the select bar first, where the eye starts; the
              count after them. */}
          {results.length > 0 ? (
            <button
              type="button"
              className={`amz__dedupe${dedupe ? ' amz__dedupe--on' : ''}`}
              role="switch"
              aria-checked={dedupe}
              onClick={() => setDedupe(!dedupe)}
              title="Flavours, sizes and multipacks of one product show as one card"
            >
              <span className="amz__dedupe-track" aria-hidden="true">
                <span className="amz__dedupe-knob" />
              </span>
              One per product line
            </button>
          ) : null}
          {results.length > 0 ? (
            <div className="web-select" role="group" aria-label="Pick main pictures">
              <span className="web-select__label">Select</span>
              {selectSteps.map((count) => (
                <button key={count} type="button" className="web-select__step" onClick={() => pickFirst(count)}>
                  {count}
                </button>
              ))}
              <button type="button" className="web-select__step" onClick={() => pickFirst(shown.length)}>
                All
              </button>
              <span className="web-select__of">in the page</span>
            </div>
          ) : null}
          <p className="amz__meta">
            {dedupe && shown.length < results.length
              ? `${shown.length} product lines from ${results.length} products`
              : `${results.length} products`}{' '}
            for “{asked.term}” on {marketOf(asked.market).domain}
          </p>
        </div>
      ) : null}

      {searching && results.length === 0 ? (
        // Everything below the bar, until there is something to put there.
        <div className="amz__waiting">
          <WwLoaderFill label="Searching Amazon" size="large" />
        </div>
      ) : null}

      <div className={`amz__grid${paint.active ? ' amz__grid--painting' : ''}`}>
        {shown.map((product) => {
          const key = pictureKey(product.image)
          const on = picked.has(key)
          const brand = brands.get(product.asin) || product.brand
          return (
            <div
              key={product.asin}
              className={`amz-card${on ? ' amz-card--on' : ''}`}
              role="button"
              tabIndex={0}
              aria-pressed={on}
              aria-label={`${on ? 'Unpick' : 'Pick'} ${product.title}`}
              onPointerDown={(event) => paint.onPointerDown(product, on, event)}
              onPointerEnter={() => paint.onPointerEnter(product, on)}
              onClick={() => {
                // A sweep ends with a click on the card it finished over,
                // which the sweep has already dealt with.
                if (paint.shouldSwallowClick()) return
                toggle(key, heldForCard(product))
              }}
              onKeyDown={(event) => {
                if (event.key === ' ') {
                  event.preventDefault()
                  toggle(key, heldForCard(product))
                }
                if (event.key === 'Enter') setOpen(product)
              }}
            >
              <div className="amz-card__media">
                <img src={product.image} alt="" loading="lazy" decoding="async" draggable={false} />
                <button
                  type="button"
                  className="amz-card__open"
                  // Its own press: never the start of a sweep, never a pick.
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation()
                    setOpen(product)
                  }}
                >
                  View details
                </button>
                <button
                  type="button"
                  className="amz-card__view"
                  title="View larger"
                  aria-label={`View ${product.title} larger`}
                  // Its own press, as View details: never a sweep, never a pick.
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation()
                    setViewing(product.image)
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true">
                    <path
                      d="M8.5 1.5h4v4M12.5 1.5L8 6M5.5 12.5h-4v-4M1.5 12.5L6 8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <span className="amz-tick" aria-hidden="true" data-on={on}>
                ✓
              </span>
              {hiddenBehind.get(product.asin) ? (
                <span
                  className="amz-card__more"
                  title="Other flavours, sizes or packs of this product, hidden by One per product line"
                >
                  +{hiddenBehind.get(product.asin)}
                </span>
              ) : null}
              <div className="amz-card__text">
                {brand ? <span className="amz-card__brand">{brand}</span> : null}
                <span className="amz-card__title">{product.title}</span>
              </div>
            </div>
          )
        })}
      </div>

      {searching && results.length > 0 ? (
        // The next page on its way: the same loader, as a band under the grid.
        <div className="amz__waiting amz__waiting--more">
          <WwLoaderFill label="Loading more" size="large" />
        </div>
      ) : null}

      {asked && page < totalPages && !searching ? (
        <div className="amz__more">
          <button
            type="button"
            className="ww-btn ww-btn--secondary"
            disabled={searching}
            onClick={() => void run(asked, page + 1, Math.min(MORE_PAGES, totalPages - page))}
          >
            {searching ? 'Loading…' : 'Load more'}
          </button>
          <span>
            Page {page} of {totalPages} ·{' '}
            {together.current && totalPages - page > 1
              ? `${Math.min(MORE_PAGES, totalPages - page)} more pages, ${Math.min(MORE_PAGES, totalPages - page)} requests`
              : 'one more page, one request'}
          </span>
        </div>
      ) : null}

      {scrolled && !open ? (
        <button
          type="button"
          className={`web-totop${picked.size > 0 ? ' web-totop--raised' : ''}`}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <span aria-hidden="true">&#8593;</span>
          Top
        </button>
      ) : null}

      {viewing ? <PictureViewer src={viewing} full={fullSize(viewing)} onClose={() => setViewing(null)} /> : null}

      {open && asked ? (
        <ProductView
          market={asked.market}
          found={open}
          picked={picked}
          onToggle={toggle}
          onSetMany={(entries, on) =>
            setPicked((was) => {
              const next = new Map(was)
              for (const [key, held] of entries) {
                if (on) next.set(key, held)
                else next.delete(key)
              }
              return next
            })
          }
          onBrand={(asin, brand) =>
            setBrands((was) => (brand && !was.has(asin) ? new Map(was).set(asin, brand) : was))
          }
          onClose={() => setOpen(null)}
        />
      ) : null}

      {picked.size > 0 ? (
        <div className="web-tray amz__tray" role="region" aria-label="Picked pictures">
          {status ? (
            <div className={`web-tray__status${status.kind === 'err' ? ' web-tray__status--err' : ''}`}>
              <span>{status.text}</span>
              <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={dismiss}>
                Dismiss
              </button>
            </div>
          ) : null}
          <div className="web-tray__row">
            <span className="web-tray__count">
              {picked.size} <span>picked</span>
            </span>
            <div className="web-tray__thumbs">
              {shownTray.map(([key, held]) => (
                <button
                  key={key}
                  type="button"
                  className="web-tray__thumb amz__tray-thumb"
                  title="Remove"
                  onClick={() => toggle(key, held)}
                >
                  <img src={held.thumb} alt="" loading="lazy" />
                  <span className="web-tray__remove">×</span>
                </button>
              ))}
              {picked.size > shownTray.length ? (
                <span className="web-tray__more">+{picked.size - shownTray.length}</span>
              ) : null}
            </div>
            <div className="web-tray__actions">
              <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={() => setPicked(new Map())}>
                Clear
              </button>
              <button
                type="button"
                className="ww-btn ww-btn--primary ww-btn--sm"
                disabled={sending}
                onClick={() => void send()}
              >
                {sending ? 'Sending…' : 'Send to Miro'}
              </button>
              <button type="button" className="ww-btn ww-btn--secondary ww-btn--sm" onClick={copyToProject}>
                Copy to project
              </button>
              <label className="web-tray__library" title="The pictures also go into Audit, as they are, tagged by what was searched">
                <input type="checkbox" checked={toLibrary} onChange={(event) => setToLibrary(event.target.checked)} />
                Also add to Internal Library
              </label>
            </div>
          </div>
        </div>
      ) : status ? (
        <div className="web-tray amz__tray" role="status">
          <div className={`web-tray__status${status.kind === 'err' ? ' web-tray__status--err' : ''}`}>
            <span>{status.text}</span>
            <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={dismiss}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default AmazonPage

/* ------------------------------------------------------------------ *
 * One product, open
 * ------------------------------------------------------------------ */

/**
 * The product, taking over the page: the picture being looked at as large as
 * the left of the screen allows, every other picture down the right.
 *
 * The main picture is already in hand from the search, so it goes up on the
 * left at once; only the right waits for the product, with the loader filling
 * it. Closing -- the button, or Escape -- goes back to the results exactly
 * where they were, ticks and all: this is drawn over them, not instead of
 * them.
 */
export function ProductView({
  market,
  found,
  picked,
  onToggle,
  onSetMany,
  onBrand,
  onClose,
}: {
  market: Market
  found: FoundProduct
  picked: Map<string, Held>
  onToggle: (key: string, held: Held) => void
  onSetMany: (entries: [string, Held][], on: boolean) => void
  onBrand: (asin: string, brand: string) => void
  onClose: () => void
}) {
  const [product, setProduct] = useState<OpenedProduct | null>(null)
  const [failed, setFailed] = useState('')
  const [attempt, setAttempt] = useState(0)
  /** Which picture is on the left, by its key. The main one until one is chosen. */
  const [shown, setShown] = useState(pictureKey(found.image))
  const side = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const leaving = new AbortController()
    setFailed('')
    openProduct(market, found.asin, leaving.signal)
      .then((opened) => {
        setProduct(opened)
        onBrand(found.asin, opened.brand)
      })
      .catch((caught: unknown) => {
        if (leaving.signal.aborted) return
        setFailed(caught instanceof Error ? caught.message : 'The product could not be opened')
      })
    return () => leaving.abort()
    // onBrand is a fresh function on every render of the page; the product is
    // what decides when to ask.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, found.asin, attempt])

  /** Every picture, the main one first, keyed the way ticks are. */
  const pictures = useMemo(() => {
    const list = product?.pictures ?? [{ url: fullSize(found.image), kind: 'main' as const, name: '' }]
    return list.map((one) => ({ ...one, key: pictureKey(one.url) }))
  }, [product, found.image])

  const current = pictures.find((one) => one.key === shown) ?? pictures[0]
  const heldFor = useCallback(
    (url: string): Held => ({
      url,
      asin: found.asin,
      title: product?.title || found.title,
      market,
      thumb: url,
    }),
    [found.asin, found.title, market, product?.title],
  )

  // Escape closes; the arrows walk through this product's pictures, which
  // costs nothing -- unlike walking to the next product would.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      const at = pictures.findIndex((one) => one.key === shown)
      const step = event.key === 'ArrowRight' ? 1 : -1
      const next = pictures[(Math.max(0, at) + step + pictures.length) % pictures.length]
      if (next) {
        event.preventDefault()
        setShown(next.key)
        side.current
          ?.querySelector<HTMLElement>(`[data-key="${CSS.escape(next.key)}"]`)
          ?.scrollIntoView({ block: 'nearest' })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, pictures, shown])

  // The page behind does not scroll while this is over it.
  useEffect(() => {
    const before = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = before
    }
  }, [])

  /**
   * The pictures in two columns, each going to whichever is shorter so far.
   *
   * CSS columns did this for free and broke as soon as there were enough
   * pictures to scroll: given a height, a multi-column box does not grow
   * downwards, it adds a third column off to the side. So the columns are
   * laid out here. A picture's shape is read from its address where Amazon
   * put it there (the A+ crops), and from the picture itself once it has
   * loaded; until then it counts as square, which the gallery mostly is.
   */
  const [shapes, setShapes] = useState<Map<string, number>>(new Map())
  const columns = useMemo(() => {
    const out: (typeof pictures)[] = [[], []]
    const height = [0, 0]
    for (const one of pictures) {
      const tall = shapes.get(one.key) ?? shapeFromAddress(one.url) ?? 1
      const into = height[0] <= height[1] ? 0 : 1
      out[into].push(one)
      height[into] += tall
    }
    return out
  }, [pictures, shapes])
  const measured = (key: string, image: HTMLImageElement) => {
    if (!image.naturalWidth) return
    const tall = image.naturalHeight / image.naturalWidth
    setShapes((was) => (Math.abs((was.get(key) ?? 0) - tall) < 0.01 ? was : new Map(was).set(key, tall)))
  }

  const allOn = product !== null && pictures.every((one) => picked.has(one.key))
  const currentOn = current ? picked.has(current.key) : false

  return (
    <div
      className={`amz-view${picked.size > 0 ? ' amz-view--tray' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={found.title}
    >
      <div className="amz-view__stage">
        {current ? (
          <img key={current.key} className="amz-view__big" src={current.url} alt={current.name || found.title} />
        ) : null}
        {current ? (
          <button
            type="button"
            className={`amz-view__pick${currentOn ? ' amz-view__pick--on' : ''}`}
            aria-pressed={currentOn}
            onClick={() => onToggle(current.key, heldFor(current.url))}
          >
            {currentOn ? '✓ Picked' : 'Pick this picture'}
          </button>
        ) : null}
      </div>

      <aside className="amz-view__side" ref={side}>
        {product ? (
          <>
            <header className="amz-view__head">
              {product.brand ? <span className="amz-view__brand">{product.brand}</span> : null}
              <h2 className="amz-view__title">{product.title || found.title}</h2>
              <div className="amz-view__row">
                <span className="amz-view__count">{pictures.length} pictures</span>
                <button
                  type="button"
                  className="ww-btn ww-btn--secondary ww-btn--sm"
                  onClick={() =>
                    onSetMany(
                      pictures.map((one) => [one.key, heldFor(one.url)] as [string, Held]),
                      !allOn,
                    )
                  }
                >
                  {allOn ? 'Untick all' : 'Tick all'}
                </button>
              </div>
            </header>
            <div className="amz-view__thumbs">
              {columns.map((column, index) => (
                <div key={index} className="amz-view__column">
              {column.map((one) => {
                const on = picked.has(one.key)
                return (
                  <div
                    key={one.key}
                    data-key={one.key}
                    className={`amz-thumb${one.key === current?.key ? ' amz-thumb--shown' : ''}${
                      on ? ' amz-thumb--on' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="amz-thumb__show"
                      title={one.name || undefined}
                      aria-label="Show this picture"
                      onClick={() => setShown(one.key)}
                    >
                      <img
                        src={one.url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                        onLoad={(event) => measured(one.key, event.currentTarget)}
                      />
                    </button>
                    <button
                      type="button"
                      className="amz-tick"
                      aria-pressed={on}
                      aria-label={on ? 'Untick this picture' : 'Tick this picture'}
                      onClick={() => onToggle(one.key, heldFor(one.url))}
                    >
                      ✓
                    </button>
                  </div>
                )
              })}
                </div>
              ))}
            </div>
          </>
        ) : failed ? (
          <div className="amz-view__failed">
            <p>{failed}</p>
            <button type="button" className="ww-btn ww-btn--secondary ww-btn--sm" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : (
          // The whole right side, until the pictures are here.
          <WwLoaderFill label="Opening the product" size="large" />
        )}
      </aside>

      <button type="button" className="amz-view__close" onClick={onClose} aria-label="Close" title="Close (Esc)">
        ×
      </button>
    </div>
  )
}

/** Height over width, where the address says how Amazon cropped it. */
function shapeFromAddress(url: string): number | null {
  const found = url.match(/_CR\d+,\d+,(\d+),(\d+)_/) ?? url.match(/_SR(\d+),(\d+)_/)
  if (!found) return null
  const width = Number(found[1])
  const height = Number(found[2])
  return width > 0 && height > 0 ? height / width : null
}
